// UseOfFundsDonut.jsx
import { useEffect, useRef, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { CardText } from "react-bootstrap";
import gsap from "gsap";

const fmt = (n) => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    return `${sign}$${parseFloat((abs / 1_000_000).toFixed(2))}M`;
  } else if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  }
  return `${sign}$${abs.toLocaleString()}`;
};

const RAW_DATA = [
  { name: "Production",               value: 40, amount: 12_000_000 },
  { name: "Above-the-Line",           value: 25, amount: 7_500_000 },
  { name: "Post-Production",          value: 15, amount: 4_500_000 },
  { name: "Marketing & Distribution", value: 12, amount: 3_600_000 },
  { name: "Contingency & Insurance",  value: 8,  amount: 2_400_000 },
];

const TAX_DATA = [
  { label: "Base Grant - 25% ", 
    description: "money for filming in texas", 
    amount: 7_500_000 },

  { label: "Rural Filming Grant - 2.5% ",
    description: "money for filming in texas", 
    amount: 750_000 },

  { label: "Post-Production Bonus - 1% ", 
    description: "money for filming in texas", 
    amount: 300_000 },

  { label: "TOTAL INCENTIVE: ",
    
    amount: 8_550_000 },
];

const TOTAL_BUDGET = 30_000_000;

const COLORS = [
  "#1a1a1a",
  "#222222",
  "#2a2a2a",
  "#333333",
  "#3a3a3a"
];

export default function UseOfFundsDonut() {
  const chartRef = useRef();
  const aberrationRefs = useRef([]);
  const centerCircleRef = useRef();
  const introTlRef = useRef();
  const activeIndexRef = useRef(null);
  const lockedIndexRef = useRef(null);

  const [activeIndex, setActiveIndex] = useState(null);
  const [lockedIndex, setLockedIndex] = useState(null);
  const [introActive, setIntroActive] = useState(null);
  const [taxActive, setTaxActive] = useState(null);
  const [lockedTaxIndex, setLockedTaxIndex] = useState(null);

  activeIndexRef.current = activeIndex;
  lockedIndexRef.current = lockedIndex;
  
  const MAX_RING_AMOUNT = Math.max(...RAW_DATA.map(d => d.amount)); // 12_000_000
  const INNER_HOLE = 100;
  const MAX_OUTER = 180;
  const availableThickness = MAX_OUTER - INNER_HOLE;
  const total = RAW_DATA.reduce((sum, d) => sum + d.value, 0);

  const sortedData = [...RAW_DATA]
    .map((d, i) => ({ ...d, originalIndex: i }))
    .sort((a, b) => a.value - b.value);

  let currentRadius = INNER_HOLE;
  const ringDimensions = sortedData.map(item => {
    const thickness = (item.value / total) * availableThickness;
    const ring = {
      inner: currentRadius,
      outer: currentRadius + thickness,
      originalIndex: item.originalIndex,
    };
    currentRadius += thickness;
    return ring;
  });

  const getScaleForIndex = (originalIndex) => {
    if (originalIndex === null) return 1;
    const sortedIndex = sortedData.findIndex(d => d.originalIndex === originalIndex);
    const scaleMin = 0.5;
    const scaleMax = 1.4;
    return scaleMin + (sortedIndex / (RAW_DATA.length - 1)) * (scaleMax - scaleMin);
  };

  const getDir = (i) => (i % 2 === 0 ? 1 : -1);

  const pauseIntro = () => {
    introTlRef.current?.pause();
    setIntroActive(null);
  };

  const resumeIntro = () => {
    if (activeIndexRef.current === null && lockedIndexRef.current === null) {
      introTlRef.current?.resume();
    }
  };

  // Intro animation
  useEffect(() => {
    const order = [...RAW_DATA]
      .map((d, i) => ({ ...d, i }))
      .sort((a, b) => b.value - a.value)
      .map(d => d.i);

    let pollId;
    let timerId;

    const runLoop = () => {
      const tl = gsap.timeline({
        repeat: 0,
        onComplete: () => setIntroActive(0)
      });
      introTlRef.current = tl;

      order.forEach((idx) => {
        tl.call(() => setIntroActive(idx))
          .to({}, { duration: 1.2 })
          .call(() => setIntroActive(null))
          .to({}, { duration: 0.4 });
      });
    };

    const poll = () => {
      const svg = chartRef.current?.container || chartRef.current;
      const pies = svg?.querySelectorAll(".recharts-pie");
      if (!pies?.length) {
        pollId = requestAnimationFrame(poll);
        return;
      }
      timerId = setTimeout(runLoop, 100);
    };

    pollId = requestAnimationFrame(poll);

    return () => {
      cancelAnimationFrame(pollId);
      clearTimeout(timerId);
      introTlRef.current?.kill();
    };
  }, []);

  // Ring + center circle GSAP reactions
  useEffect(() => {
    if (!chartRef.current) return;

    const timer = setTimeout(() => {
      const svg = chartRef.current?.container || chartRef.current;
      const pies = svg?.querySelectorAll(".recharts-pie");
      if (!pies?.length) return;

      const activeOrLocked = activeIndex ?? lockedIndex ?? introActive;

      pies.forEach((pie, i) => {
        const ringOriginalIndex = ringDimensions[i].originalIndex;
        const isActive = activeOrLocked === ringOriginalIndex;
        const dir = getDir(i);

        gsap.killTweensOf(pie);

        if (isActive) {
          gsap.to(pie, { rotate: 12 * dir, transformOrigin: "50% 50%", duration: 0.6, ease: "back.out(2.2)" });
        } else {
          gsap.to(pie, { rotate: 0, transformOrigin: "50% 50%", duration: 0.4, ease: "power2.out" });
        }
      });

      gsap.killTweensOf(centerCircleRef.current);
      if (activeOrLocked !== null) {
        gsap.to(centerCircleRef.current, { scale: getScaleForIndex(activeOrLocked), transformOrigin: "50% 50%", duration: 0.5, ease: "back.out(2.2)" });
      } else {
        gsap.to(centerCircleRef.current, { scale: 1, duration: 0.4, ease: "power2.out" });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activeIndex, lockedIndex, introActive]);

return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>

      <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "center", }}>

        {/* LEFT COLUMN — title, legend */}
        <div style={{ 
          flex: .7, 
          display: "flex", 
          flexDirection: "column",
          justifyContent: "center", 
          alignItems: "center", 
          gap: "1rem",
          
          }}>
        <div 
        style={{
          flex: 1, display: "flex", flexDirection: "column", 
          textAlign: "center" 
        }}
        >
           <h10>Budget</h10>
          <h10>$30M</h10>
        </div>
          {/* LEGEND */}
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            width: "100%",
            alignItems: "flex-start",
            // paddingLeft: "1rem",
          }}>
            {RAW_DATA.map((entry, i) => (
              <span
                key={entry.name}
                onMouseEnter={() => { pauseIntro(); setActiveIndex(i); }}
                onMouseLeave={() => { setActiveIndex(null); resumeIntro(); }}
                onClick={() => { pauseIntro(); setLockedIndex(lockedIndex === i ? null : i); }}
                style={{
                  cursor: "pointer",
                  opacity: activeIndex === i || lockedIndex === i || introActive === i ? 1 : 0.7,
                  transition: "opacity 0.2s",
                  paddingLeft: "1rem"
                }}
              >
              
                <span style={{
                  color: 'silver',
                  fontFamily: "'Buzzer', sans-serif",
                  fontSize: 'clamp(1.5rem, 5vw, 1.5rem)'
                }}>
                  {entry.name}
                </span>
              </span>
            ))}
          </div>

        </div>

        {/* CENTER — chart */}
        <div
          ref={chartRef}
          style={{
            flex: 1.2,
            position: "relative",
            height: 420,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ResponsiveContainer>
            <PieChart>
              {ringDimensions.map((ring, i) => {
                const entry = RAW_DATA[ring.originalIndex];
                return (
                  <Pie
                    key={entry.name}
                    data={[entry]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    startAngle={0}
                    endAngle={360}
                    innerRadius={ring.inner}
                    outerRadius={ring.outer}
                    stroke="rgba(255, 255, 255, 0.07)"
                    strokeWidth={1}
                    isAnimationActive={false}
                    onMouseEnter={() => { pauseIntro(); setActiveIndex(ring.originalIndex); }}
                    onMouseLeave={() => { setActiveIndex(null); resumeIntro(); }}
                    onClick={() => { pauseIntro(); setLockedIndex(lockedIndex === ring.originalIndex ? null : ring.originalIndex); }}
                  >
                    <Cell fill={COLORS[ring.originalIndex]} />
                  </Pie>
                );
              })}
            </PieChart>
          </ResponsiveContainer>

          {/* Chromatic aberration layers */}
          {RAW_DATA.map((_, i) => (
            <div
              key={i}
              ref={(el) => (aberrationRefs.current[i] = el)}
              style={{
                position: "absolute",
                width: ringDimensions[i].outer * 2,
                height: ringDimensions[i].outer * 2,
                borderRadius: "50%",
                border: "1px solid rgba(255,0,80,0.2)",
                pointerEvents: "none",
                mixBlendMode: "screen",
                opacity: 0,
                transform: "translate(-50%, -50%)"
              }}
            />
          ))}

          {/* Static backdrop */}
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: INNER_HOLE * 2 + 4,
            height: INNER_HOLE * 2 + 4,
            borderRadius: "50%",
            backgroundColor: "rgba(41, 40, 40, 0.95)",
            pointerEvents: "none",
            zIndex: 1,
          }} />

          {/* Tax coverage fill circle */}
         {/* Tax coverage fill circle */}
{(taxActive !== null || lockedTaxIndex !== null) && (
  <div style={{
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: MAX_OUTER * 2 * (TAX_DATA[taxActive ?? lockedTaxIndex].amount / MAX_RING_AMOUNT),
    height: MAX_OUTER * 2 * (TAX_DATA[taxActive ?? lockedTaxIndex].amount / MAX_RING_AMOUNT),
    borderRadius: "50%",
    backgroundColor: "rgba(243, 61, 61, 0.35)",
    border: "1px solid rgba(243, 61, 61, 0.7)",
    pointerEvents: "none",
    zIndex: 3,
    transition: "width 0.4s ease, height 0.4s ease",
  }} />
)}

          {/* Scaling circle */}
          <div
            ref={centerCircleRef}
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: INNER_HOLE * 2,
              height: INNER_HOLE * 2,
              borderRadius: "50%",
              backgroundColor: "rgba(20,19,19,0.95)",
              backdropFilter: "blur(40px)",
              border: "1px solid rgba(255,255,255,0.1)",
              pointerEvents: "none",
              zIndex: 2,
            }}
          />

          {/* Center focus display */}
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "white",
            textAlign: "center",
            pointerEvents: "none",
            width: 180,
            zIndex: 4,
          }}>
            {(activeIndex ?? lockedIndex ?? introActive) !== null && (
              <>
               <div style={{ fontFamily: "'Buzzer', sans-serif",
                fontSize: 'clamp(1.5rem, 5vw, 1.5rem)', fontWeight: "bold" }}>
                  {RAW_DATA[(activeIndex ?? lockedIndex ?? introActive)].name}
                </div>
                  <div style={{ fontSize: 'clamp(1.4rem, 5.2vw, 1.4rem)', fontWeight: "bold", opacity: 0.7  }}>
                  {RAW_DATA[(activeIndex ?? lockedIndex ?? introActive)].value}%
                </div>
                 <div style={{ fontSize: 'clamp(1.5rem, 5vw, 1.5rem)', fontWeight: "bold",}}>
                  {fmt(RAW_DATA[(activeIndex ?? lockedIndex ?? introActive)].amount)}
                </div>
              </>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN — tax incentives */}
        <div style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: "1rem", 
        }}>
          {/* <h10 style={{ textAlign: 'center', marginBottom: '0rem' }}>
            Texas Moving Image Industry Incentives
          </h10> */}
        <div 
        style={{
          flex: 1, display: "flex", flexDirection: "column", 
          textAlign: "center" 
        }}
        >
           <h10>Tax Incentives</h10>
          <h10>$8.55M</h10>
        </div>
          <div style={{ 
             display: "flex",
            flexDirection: "column",
            gap: "1rem",
            width: "100%",
            alignItems: "flex-start",
            }}>
            {TAX_DATA.map((item, i) => (
              <span
                key={i}
                onMouseEnter={() => { pauseIntro(); setTaxActive(i); }}
                onMouseLeave={() => { setTaxActive(null); resumeIntro(); }}
                onClick={() => setLockedTaxIndex(lockedTaxIndex === i ? null : i)}

                style={{
                  cursor: "pointer",
                  opacity: taxActive === i || lockedTaxIndex === i ? 1 : 0.7,
                  transition: "opacity 0.2s",
                }}
              >
                <div>
                  <span
                  style={{
                  color: 'silver',
                  fontFamily: "'Buzzer', sans-serif",
                  fontSize: 'clamp(1.5rem, 5vw, 1.5rem)',

                }}
                  > 
                    {item.label} 
                   {fmt(item.amount)}
                  </span>
               
                </div>
                {/* <div>{item.description}</div> */}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}